<template>
  <v-hover v-slot="{ isHovering, props: hoverProps }">
    <v-row class="ma-1 test-row-content" v-bind="{ ...$attrs, ...hoverProps }">
      <v-col cols="1" class="px-1 pb-1 pt-0 align-self-end text-center">
        <span v-if="row.line">{{ formatLineNumber(row) }}</span>
      </v-col>
      <v-col cols="10" class="pa-0">
        <v-container v-if="row.type === 'Line'" class="pa-0 ma-0">
          <v-row
            v-if="row.signs && row.signs.length > 0"
            class="pa-0 ma-0 mb-1 align-end"
          >
            <div
              v-for="(sign, index) in row.signs"
              :key="index"
              class="text-h5"
              :class="{ 'pr-2': sign.post === ' ' }"
            >
              <v-row
                class="pa-0 ma-0 align-center"
                :class="{ 'selected-sign': index === row.selectedSign }"
              >
                <v-img
                  v-if="sign.type === 'image'"
                  :src="signVectorUrl(sign.code || '')"
                  height="25px"
                  :width="getWidth(signVectorUrl(sign.code || '')) || 30"
                  class="d-inline-block"
                />
                <span
                  v-else-if="sign.type === 'utf8'"
                  class="my-n1 mx-1 cuneiform"
                  >{{ getSignHTMLCode(sign.code || '') }}</span
                >
                <v-icon
                  v-else-if="!sign.type"
                  color="red"
                  class="ma-1"
                  size="small"
                  >mdi-block-helper</v-icon
                >
              </v-row>
              <v-row
                class="pa-0 text-body-2 ma-0 text-grey justify-space-between"
              >
                <span>{{ ' ' }} </span>
                <span v-if="sign.reading !== '@'">
                  {{ sign.reading || '' }}
                </span>
                <span v-else>...</span>
                <span>
                  {{ sign.post && sign.post !== '%' ? sign.post : ' ' }}
                </span>
              </v-row>
            </div>
          </v-row>
          <v-row class="pa-0 ma-0">
            <v-textarea
              placeholder="Enter text here"
              auto-grow
              rows="1"
              class="mx-1 mt-0 mb-n5 hide-line test-line-text"
              :autofocus="autofocus"
              :variant="outlined ? 'outlined' : 'plain'"
              @keydown.enter.prevent
              @keyup.enter="$emit('add-row-after')"
              @update:model-value="typeText($event)"
              @keyup="updateSignSelection($event)"
              @click="updateSignSelection($event)"
              @focus="setFocused"
              @blur="resetSignSelection"
              :model-value="localText"
              ref="textareaRef"
              density="compact"
            />
          </v-row>
          <v-container
            v-if="markupErrors.length > 0 && !restrictToSign && !restrictToWord"
            class="pa-0 ma-0 my-2"
          >
            <v-row
              v-for="(error, idx) in markupErrors"
              :key="idx"
              class="ma-0 pa-0"
            >
              <v-icon color="red" class="ma-1" size="small"
                >mdi-block-helper</v-icon
              >
              <span class="text-red"
                >{{ error.error }}
                <b v-if="error.text">{{ error.text }}</b>
              </span>
            </v-row>
          </v-container>
          <v-container v-if="hasWordRestrictionError" class="pa-0 ma-0 my-2">
            <v-icon color="red" class="ma-1" size="small"
              >mdi-block-helper</v-icon
            >
            <span class="text-red"
              >Only one word can be entered with no markup. Please remove all
              whitespace and markup characters and try again.
            </span>
          </v-container>
          <v-container v-if="hasSignRestrictionError" class="pa-0 ma-0 my-2">
            <v-icon color="red" class="ma-1" size="small"
              >mdi-block-helper</v-icon
            >
            <span class="text-red"
              >Only one sign can be entered with no markup. Please remove all
              separator and markup characters and try again.
            </span>
          </v-container>
        </v-container>
        <v-row
          class="pa-0 ma-0"
          v-else-if="
            row.type === 'Broken Line(s)' ||
            row.type === 'Uninscribed Line(s)' ||
            row.type === 'Ruling(s)'
          "
        >
          <v-col v-show="row.isEditing" cols="2" class="ma-0 pa-0">
            <v-combobox
              v-model="regionInput"
              class="mx-1 mt-n1 mb-n3 pt-0 test-region-value"
              hide-details
              :items="getRegionItems(row.type)"
              @keydown.enter="setLineValue(regionInput)"
              @keydown.esc="cancelLineValue"
              @update:focused="!$event && setLineValue(regionInput)"
              single-line
              placeholder="Select"
            >
              <template #item="{ props: itemProps, item }">
                <v-list-item v-bind="itemProps" @click="setLineValue(item)" />
              </template>
            </v-combobox>
          </v-col>
          <span
            v-if="row.type === 'Ruling(s)'"
            v-show="!row.isEditing"
            class="mr-1"
            >{{ row.lineValue ? formatRuling(row.lineValue) : 'Single' }}</span
          >
          <span v-else v-show="!row.isEditing" class="mr-1"
            >{{ row.lineValue || 1 }}
          </span>
          <v-col cols="4" class="ma-0 pa-0">
            <span class="my-1"
              >{{ `${row.type}` }}
              <a @click="toggleRowEditing()" v-if="!row.isEditing" class="ml-4"
                >Edit</a
              >
            </span>
          </v-col>
        </v-row>
        <v-row class="pa-0 ma-0" v-else-if="row.type === 'Seal Impression'">
          <span class="my-1"
            >{{ row.type }} {{ row.reading }}
            <v-textarea
              v-if="row.isEditing"
              v-model="sealReading"
              placeholder="Enter value here"
              auto-grow
              rows="1"
              class="mx-1 mt-0 mb-n5 hide-line d-inline-block"
              :autofocus="true"
              variant="plain"
              @blur="setSealImpressionReading(sealReading)"
              @keydown.enter.prevent="setSealImpressionReading(sealReading)"
              density="compact"
            />
            <a @click="toggleRowEditing()" v-if="!row.isEditing" class="ml-2">{{
              row.reading ? 'Edit Label' : 'Add Label'
            }}</a>
          </span>
        </v-row>
        <span v-else class="my-1">{{ row.type }}</span>
      </v-col>
      <v-col v-if="showDeleteButton" cols="1" class="pa-0 text-right">
        <v-btn @click="$emit('remove-row')" icon class="ma-1" size="x-small">
          <v-icon :color="isHovering ? 'red' : 'transparent'"
            >mdi-delete</v-icon
          >
        </v-btn>
      </v-col>
    </v-row>
  </v-hover>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  nextTick,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue';
import { RowWithLine } from './Column.vue';
import { formatLineNumber as defaultLineFormatter } from '@oare/oare';
import sl from '@/serviceLocator';
import signVectorUrl from '@/utils/signVectors';
import { v4 } from 'uuid';
import { RowTypes, EditorMarkupError } from '@oare/types';
import EventBus, { ACTIONS } from '@/EventBus';
import {
  getRowSigns,
  MARKUP_CHARS_PATTERN,
  RowSignsOptions,
} from '../../utils/rowSigns';

// A restore with the backend down fails every row's lookup at once. Each
// row shows its own error, but restored rows raise the snackbar at most once
// per burst: a background failure within 10 s of the last one stays quiet.
// A failure while typing always raises it.
const RESTORE_ERROR_QUIET_MS = 10000;
let lastRestoreErrorAt = Number.NEGATIVE_INFINITY;
const shouldReportLookupError = (background: boolean) => {
  if (!background) {
    return true;
  }
  const now = Date.now();
  const report = now - lastRestoreErrorAt > RESTORE_ERROR_QUIET_MS;
  lastRestoreErrorAt = now;
  return report;
};

export default defineComponent({
  inheritAttrs: false,
  props: {
    row: {
      type: Object as PropType<RowWithLine>,
      required: true,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    isCurrentRow: {
      type: Boolean,
      default: false,
    },
    showDeleteButton: {
      type: Boolean,
      default: true,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    restrictToWord: {
      type: Boolean,
      default: false,
    },
    restrictToSign: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'add-row-after',
    'remove-row',
    'update-row-content',
    'reset-new-row',
    'set-current-row',
    'set-focused',
    'edited',
  ],
  setup(props, { emit }) {
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');

    const textareaRef = ref();

    const markupErrors = ref<EditorMarkupError[]>([]);
    const hasWordRestrictionError = ref(false);
    const hasSignRestrictionError = ref(false);

    const formatLineNumber = (row: RowWithLine) => {
      if ((!row.lineValue && row.line) || (row.line && row.lineValue === 1)) {
        return defaultLineFormatter(row.line);
      }
      if (row.lineValue && row.line) {
        const formattedFirstNumber = defaultLineFormatter(
          row.line - row.lineValue + 1,
          false
        );
        const formattedLastNumber = defaultLineFormatter(row.line);
        return `${formattedFirstNumber} - ${formattedLastNumber}`;
      }
    };

    const getWidth = (src: string) => {
      const image = new Image();
      image.src = src;
      const heightRatio = 25 / image.height;
      return heightRatio * image.width;
    };

    const getSignHTMLCode = (code: string) => {
      let codePt = Number(`0x${code}`);
      if (codePt > 0xffff) {
        codePt -= 0x10000;
        return String.fromCharCode(
          0xd800 + (codePt >> 10),
          0xdc00 + (codePt & 0x3ff)
        );
      } else {
        return String.fromCharCode(codePt);
      }
    };

    const resetSignSelection = () => {
      focused.value = false;
      emit('reset-new-row');
      emit('update-row-content', {
        ...props.row,
        selectedSign: undefined,
      });
    };

    const cursorIndex = ref(0);
    const updateSignSelection = async (event: KeyboardEvent | MouseEvent) => {
      // The listener sits on the v-textarea root, so only act when the event
      // bubbled up from the textarea itself.
      const target = event.target;
      if (!(target instanceof HTMLTextAreaElement)) {
        return;
      }
      if ((event as KeyboardEvent).key !== 'Enter') {
        const rowText: string = target.value;
        cursorIndex.value = target.selectionStart;
        if (!cursorIndex.value) {
          emit('update-row-content', {
            ...props.row,
            selectedSign: 0,
          });
        } else {
          const textBeforeCursor = rowText.slice(0, cursorIndex.value);
          const originalSigns = textBeforeCursor.split(/[\s\-.\+]+/);
          try {
            const formattedSigns = await Promise.all(
              originalSigns.map(sign => {
                if (sign !== '') {
                  return server.getFormattedSign(sign);
                }
                return [''];
              })
            );
            const signs = formattedSigns.flat();
            emit('update-row-content', {
              ...props.row,
              selectedSign: signs.length - 1,
            });
          } catch (err) {
            actions.showErrorSnackbar(
              'Error selecting sign. Please try again.',
              err as Error
            );
          }
        }
      }
    };

    const sealReading = ref('');
    const toggleRowEditing = () => {
      sealReading.value = props.row.reading || '';
      emit('update-row-content', {
        ...props.row,
        isEditing: true,
      });
    };

    // The textarea is driven by this local copy rather than by `row.text`.
    // Deriving signs is asynchronous (one server round trip per sign), and
    // when it resolves the parent hands back the text as it was at that
    // keystroke. Feeding that stale text straight into the textarea's model
    // silently deleted whatever the user typed in the meantime.
    const localText = ref(props.row.text || '');
    watch(
      () => props.row.text,
      text => {
        if ((text || '') !== localText.value) {
          localText.value = text || '';
        }
      }
    );
    let updateSequence = 0;

    // background: the row is being restored or pasted, not typed in, so its
    // sign lookup waits its turn behind other rows.
    const updateText = async (text: string, background = false) => {
      localText.value = text;
      if (
        props.restrictToWord &&
        (text.match(/\s/) || text.match(MARKUP_CHARS_PATTERN))
      ) {
        hasWordRestrictionError.value = true;
      } else {
        hasWordRestrictionError.value = false;
      }

      if (
        props.restrictToSign &&
        (text.match(/\s/) ||
          text.match(/[\-.\+%]+/) ||
          text.match(MARKUP_CHARS_PATTERN))
      ) {
        hasSignRestrictionError.value = true;
      } else {
        hasSignRestrictionError.value = false;
      }

      const newRows = text.split('\n');
      // Publish the text right away so the row never lags the textarea, then
      // derive the signs. Only the newest keystroke gets to publish its
      // derived data; earlier lookups still in flight are dropped, since the
      // text they describe is already gone. Both emits set `text` explicitly
      // because `props.row` only catches up on the next render.
      updateSequence += 1;
      const sequence = updateSequence;
      emit('update-row-content', {
        ...props.row,
        text: newRows[0],
      });
      const result = await getSigns(newRows[0], {
        background,
        isCurrent: () => sequence === updateSequence,
      });
      if (!result || sequence !== updateSequence) {
        return;
      }
      const { errors, ...derived } = result;
      markupErrors.value = errors;
      emit('update-row-content', {
        ...props.row,
        text: newRows[0],
        ...derived,
      });

      newRows.shift();
      const reversedNewRows = newRows.reverse();
      reversedNewRows.forEach(async rowText => {
        emit('add-row-after', rowText);
      });
    };

    const getSigns = async (rowText: string, options?: RowSignsOptions) => {
      const rowSigns = await getRowSigns(rowText, server, options);
      if (!rowSigns) {
        return null;
      }
      const { signs, words, errors, lookupError } = rowSigns;
      if (lookupError && (!options?.isCurrent || options.isCurrent())) {
        if (shouldReportLookupError(!!options?.background)) {
          actions.showErrorSnackbar(
            'Error generating signs. Edit the line to try again.',
            lookupError
          );
        }
        errors.push({
          error:
            'Some signs could not be looked up. Edit the line to try again.',
        });
      }

      const hasErrors =
        errors.length > 0 ||
        hasWordRestrictionError.value === true ||
        hasSignRestrictionError.value === true;

      return { signs, words, hasErrors, errors };
    };

    const getRegionItems = (type: RowTypes) => {
      if (type === 'Ruling(s)') {
        return ['Single', 'Double', 'Triple'];
      }
      return [1, 2, 3, 4, 5, 6, 7, 8];
    };

    const formatRuling = (value: number) => {
      switch (value) {
        case 1:
          return 'Single';
        case 2:
          return 'Double';
        case 3:
          return 'Triple';
        default:
          return value;
      }
    };

    // The combobox commits on Enter, on leaving the field, or on picking an
    // item, never per keystroke, so multi-digit counts can be typed. Empty or
    // non-numeric input keeps the previous value, and Escape cancels the edit.
    const regionInput = ref<string | number | null>(null);
    // Set once this edit is committed or cancelled, so the blur that follows
    // Escape or Enter cannot commit again before the row leaves edit mode.
    const lineValueClosed = ref(false);
    watch(
      () => props.row.isEditing,
      isEditing => {
        if (isEditing) {
          lineValueClosed.value = false;
        }
      }
    );
    const closeLineValueEdit = () => {
      lineValueClosed.value = true;
      regionInput.value = null;
    };
    const cancelLineValue = () => {
      if (!props.row.isEditing || lineValueClosed.value) {
        return;
      }
      closeLineValueEdit();
      emit('update-row-content', {
        ...props.row,
        isEditing: false,
      });
    };
    const setLineValue = (input: string | number | null | undefined) => {
      if (!props.row.isEditing || lineValueClosed.value) {
        return;
      }
      closeLineValueEdit();
      let value = input === null || input === undefined ? '' : String(input);
      value = value.trim();
      if (value === 'Single') {
        value = '1';
      } else if (value === 'Double') {
        value = '2';
      } else if (value === 'Triple') {
        value = '3';
      }
      const lineValue = Number(value);
      const isValid =
        value !== '' && Number.isInteger(lineValue) && lineValue > 0;
      emit('update-row-content', {
        ...props.row,
        ...(isValid ? { lineValue } : {}),
        isEditing: false,
      });
    };

    const setSealImpressionReading = (reading: string) => {
      emit('update-row-content', {
        ...props.row,
        reading,
        isEditing: false,
      });
    };

    // The user changed the text (typing or a special-character button).
    const typeText = (text: string) => {
      emit('edited');
      return updateText(text);
    };

    const insertChar = async (delineator: string) => {
      // localText, not props.row.text: the row only catches up after the
      // async sign lookup, so it may lag what the user has just typed.
      const originalRowText = localText.value;
      const textBeforeCursor = originalRowText.slice(0, cursorIndex.value);
      const textAfterCursor = originalRowText.slice(cursorIndex.value);
      const newText = `${textBeforeCursor}${delineator}${textAfterCursor}`;
      // Move the caret as soon as the text is in the textarea, before the
      // sign lookup completes, so keystrokes that follow the button click
      // land after the inserted character rather than before it.
      cursorIndex.value += delineator.length;
      const pending = typeText(newText);
      await nextTick();
      textareaRef.value.focus();
      const input = textareaRef.value.$el.querySelector('textarea');
      input.setSelectionRange(cursorIndex.value, cursorIndex.value);
      await pending;
    };

    const focused = ref(false);
    const setFocused = () => {
      focused.value = true;
      emit('set-current-row');
    };
    watch(focused, () => {
      emit('set-focused', focused.value);
    });

    onMounted(async () => {
      emit('set-current-row');
      if (props.row.text) {
        updateText(props.row.text, true);
      }
      if (
        props.row.type === 'Broken Line(s)' ||
        props.row.type === 'Broken Area' ||
        props.row.type === 'Ruling(s)' ||
        props.row.type === 'Uninscribed Line(s)'
      ) {
        emit('update-row-content', {
          ...props.row,
          regionDiscourseUuid: v4(),
        });
      }
      EventBus.on(ACTIONS.SPECIAL_CHAR_INPUT, onSpecialChar);
    });

    const onSpecialChar = async (char: string) => {
      if (props.isCurrentRow) {
        await insertChar(char);
      }
    };

    onBeforeUnmount(() => {
      EventBus.off(ACTIONS.SPECIAL_CHAR_INPUT, onSpecialChar);
      // Supersede any sign lookup still queued or in flight, so it is dropped
      // before it takes a slot and never reports into a removed row.
      updateSequence += 1;
    });

    return {
      formatLineNumber,
      getWidth,
      signVectorUrl,
      getSignHTMLCode,
      resetSignSelection,
      updateSignSelection,
      toggleRowEditing,
      updateText,
      typeText,
      getRegionItems,
      getSigns,
      formatRuling,
      setLineValue,
      regionInput,
      cancelLineValue,
      insertChar,
      textareaRef,
      localText,
      focused,
      setFocused,
      setSealImpressionReading,
      sealReading,
      markupErrors,
      hasWordRestrictionError,
      hasSignRestrictionError,
    };
  },
});
</script>

<style scoped>
.cuneiform {
  font-family: 'Santakku', 'CuneiformComposite';
}

.selected-sign {
  box-shadow: 0px -2px 0px gold inset;
}
</style>
